import { useEffect, useMemo, useRef, useState } from "react";
import {
  Activity,
  CalendarPlus,
  CheckCircle2,
  ChevronDown,
  ExternalLink,
  Mic,
  MonitorUp,
  Radio,
  ScreenShare,
  ShieldAlert,
  Square,
  Video,
} from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { useAuth } from "../../../../auth/useAuth";
import ChurchLogo from "../../../../assets/logos/Church-Logo.png";
import GlobalNotificationPill from "../../../../core/components/GlobalNotificationPill";
import useResponsive from "../../../../core/hooks/useResponsive";
import { supabase } from "../../services/churchSupabaseClient";
import { loadChurchServiceTimes } from "../../services/churchServiceTimesService";
import {
  clearChurchStreamDestination,
  createChurchYouTubeBroadcast,
  getChurchStreamAccount,
  getChurchStreamAccountId,
  loadChurchStreamWorkspace,
  saveChurchStreamDestination,
  startChurchYouTubeOAuth,
  transitionChurchYouTubeBroadcast,
} from "../../services/churchStreamService";

const SCENE_LAYOUTS = [
  { id: "slides", label: "Slides Only" },
  { id: "camera", label: "Camera Only" },
  { id: "slides-camera", label: "Slides + Camera" },
  { id: "camera-slides", label: "Camera + Slides" },
  { id: "announcements", label: "Announcements Loop" },
  { id: "countdown", label: "Service Countdown" },
];

const DEFAULT_STREAM_DRAFT = {
  title: "Sunday Worship Service",
  description: "",
  scheduledStartTime: "",
  privacyStatus: "unlisted",
};

const DEFAULT_YOUTUBE_RTMP_URL = "rtmp://a.rtmp.youtube.com/live2";
const STREAM_APP_ICON_URL = "/app-icons/church-stream-icon.svg";
const STREAM_CONTROL_CHANNEL = "oikos.church.stream-control";
const STREAM_STUDIO_IDLE_CSS = `
  .stream-studio-page button:focus,
  .stream-studio-page select:focus,
  .stream-studio-page input:focus {
    outline: none;
  }

  .stream-studio-page button:focus-visible,
  .stream-studio-page select:focus-visible,
  .stream-studio-page input:focus-visible {
    outline: none;
    box-shadow: none;
  }

  @keyframes streamIdleLogoFade {
    0%, 100% {
      opacity: 0.64;
      transform: scale(0.96);
      filter: drop-shadow(0 18px 38px rgba(37, 99, 235, 0.22));
    }
    50% {
      opacity: 1;
      transform: scale(1.04);
      filter: drop-shadow(0 26px 52px rgba(37, 99, 235, 0.38));
    }
  }

  .stream-idle-logo-frame {
    animation: streamIdleLogoFade 2.8s ease-in-out infinite;
  }

  @keyframes streamPreLivePulse {
    0%, 100% {
      opacity: 0.76;
      transform: scale(0.94);
      filter: drop-shadow(0 20px 44px rgba(37, 99, 235, 0.24));
    }
    50% {
      opacity: 1;
      transform: scale(1.08);
      filter: drop-shadow(0 34px 70px rgba(37, 99, 235, 0.42));
    }
  }

  .stream-pre-live-logo {
    animation: streamPreLivePulse 1.15s ease-in-out infinite;
  }
`;

function getWorkspaceName(user) {
  return (
    user?.churchOrgSession?.account?.name ||
    user?.account?.name ||
    user?.organization?.name ||
    "Church Workspace"
  );
}

function getWorkspaceLogo(user) {
  return (
    user?.churchOrgSession?.account?.logo_url ||
    user?.churchOrgSession?.account?.logoUrl ||
    user?.account?.logo_url ||
    user?.account?.logoUrl ||
    user?.organization?.logo_url ||
    user?.organization?.logoUrl ||
    STREAM_APP_ICON_URL ||
    ChurchLogo
  );
}

function getOrganizationLogo(user) {
  return (
    user?.churchOrgSession?.account?.logo_url ||
    user?.churchOrgSession?.account?.logoUrl ||
    user?.account?.logo_url ||
    user?.account?.logoUrl ||
    user?.organization?.logo_url ||
    user?.organization?.logoUrl ||
    ChurchLogo
  );
}

function stopStream(stream) {
  if (!stream) return;
  stream.getTracks().forEach((track) => track.stop());
}

function getRecorderMimeType() {
  if (!window.MediaRecorder) return "";
  const options = [
    "video/webm;codecs=vp8,opus",
    "video/webm;codecs=vp9,opus",
    "video/webm",
  ];
  return options.find((option) => window.MediaRecorder.isTypeSupported(option)) || "";
}

function getMediaErrorMessage(error, sourceLabel) {
  const errorName = error?.name || "";

  if (!navigator.mediaDevices?.getUserMedia) {
    return "Camera and microphone access requires a supported browser on localhost or HTTPS.";
  }

  if (errorName === "NotAllowedError" || errorName === "PermissionDeniedError") {
    return `${sourceLabel} permission was blocked. Allow access in the browser permission prompt or site settings.`;
  }

  if (errorName === "NotFoundError" || errorName === "DevicesNotFoundError") {
    return `No ${sourceLabel.toLowerCase()} device was found.`;
  }

  if (errorName === "NotReadableError" || errorName === "TrackStartError") {
    return `${sourceLabel} is already in use by another app or browser tab.`;
  }

  if (errorName === "OverconstrainedError" || errorName === "ConstraintNotSatisfiedError") {
    return `The selected ${sourceLabel.toLowerCase()} is unavailable. Choose a different device.`;
  }

  return `${sourceLabel} could not be started. Check browser permissions and device selection.`;
}

function drawPlaceholder(ctx, width, height, title, detail) {
  ctx.fillStyle = "#17251e";
  ctx.fillRect(0, 0, width, height);
  ctx.fillStyle = "rgba(255,255,255,0.08)";
  ctx.fillRect(width * 0.08, height * 0.16, width * 0.84, height * 0.68);
  ctx.fillStyle = "#ffffff";
  ctx.font = "700 54px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  ctx.textAlign = "center";
  ctx.fillText(title, width / 2, height / 2 - 12);
  ctx.font = "28px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  ctx.fillStyle = "rgba(255,255,255,0.72)";
  ctx.fillText(detail, width / 2, height / 2 + 42);
}

function drawPanelPlaceholder(ctx, x, y, width, height, title, detail) {
  ctx.fillStyle = "#101b15";
  ctx.fillRect(x, y, width, height);
  ctx.fillStyle = "rgba(37, 99, 235, 0.16)";
  ctx.fillRect(x + width * 0.08, y + height * 0.12, width * 0.84, height * 0.76);
  ctx.fillStyle = "#ffffff";
  ctx.textAlign = "center";
  ctx.font = "800 34px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  ctx.fillText(title, x + width / 2, y + height / 2 - 8);
  ctx.font = "700 20px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  ctx.fillStyle = "rgba(255,255,255,0.72)";
  ctx.fillText(detail, x + width / 2, y + height / 2 + 34);
}

function drawVideoCover(ctx, video, x, y, width, height) {
  if (!video?.videoWidth || !video?.videoHeight) return false;

  const videoRatio = video.videoWidth / video.videoHeight;
  const targetRatio = width / height;
  let drawWidth = width;
  let drawHeight = height;
  let offsetX = 0;
  let offsetY = 0;

  if (videoRatio > targetRatio) {
    drawWidth = height * videoRatio;
    offsetX = (width - drawWidth) / 2;
  } else {
    drawHeight = width / videoRatio;
    offsetY = (height - drawHeight) / 2;
  }

  ctx.drawImage(video, x + offsetX, y + offsetY, drawWidth, drawHeight);
  return true;
}

function drawVideoContain(ctx, video, x, y, width, height) {
  if (!video?.videoWidth || !video?.videoHeight) return false;

  const videoRatio = video.videoWidth / video.videoHeight;
  const targetRatio = width / height;
  let drawWidth = width;
  let drawHeight = height;
  let offsetX = 0;
  let offsetY = 0;

  if (videoRatio > targetRatio) {
    drawHeight = width / videoRatio;
    offsetY = (height - drawHeight) / 2;
  } else {
    drawWidth = height * videoRatio;
    offsetX = (width - drawWidth) / 2;
  }

  ctx.fillStyle = "#000000";
  ctx.fillRect(x, y, width, height);
  ctx.drawImage(video, x + offsetX, y + offsetY, drawWidth, drawHeight);
  return true;
}

function drawVideoContainWithSoftFill(ctx, video, x, y, width, height) {
  if (!video?.videoWidth || !video?.videoHeight) return false;

  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, width, height);
  ctx.clip();

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(x, y, width, height);

  const videoRatio = video.videoWidth / video.videoHeight;
  const targetRatio = width / height;
  let drawWidth = width;
  let drawHeight = height;
  let offsetX = 0;
  let offsetY = 0;

  if (videoRatio > targetRatio) {
    drawHeight = width / videoRatio;
    offsetY = (height - drawHeight) / 2;
  } else {
    drawWidth = height * videoRatio;
    offsetX = (width - drawWidth) / 2;
  }

  ctx.drawImage(video, x + offsetX, y + offsetY, drawWidth, drawHeight);
  ctx.restore();
  return true;
}

function drawVideoContainOnSoftPanel(ctx, video, x, y, width, height) {
  if (!video?.videoWidth || !video?.videoHeight) return false;

  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, width, height);
  ctx.clip();

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(x, y, width, height);

  const videoRatio = video.videoWidth / video.videoHeight;
  const targetRatio = width / height;
  let drawWidth = width;
  let drawHeight = height;
  let offsetX = 0;
  let offsetY = 0;

  if (videoRatio > targetRatio) {
    drawHeight = width / videoRatio;
    offsetY = (height - drawHeight) / 2;
  } else {
    drawWidth = height * videoRatio;
    offsetX = (width - drawWidth) / 2;
  }

  ctx.drawImage(video, x + offsetX, y + offsetY, drawWidth, drawHeight);
  ctx.restore();
  return true;
}

function drawImageContain(ctx, image, x, y, width, height) {
  const imageWidth = image?.naturalWidth || image?.width || 0;
  const imageHeight = image?.naturalHeight || image?.height || 0;
  if (!imageWidth || !imageHeight) return false;

  const imageRatio = imageWidth / imageHeight;
  const targetRatio = width / height;
  let drawWidth = width;
  let drawHeight = height;
  let offsetX = 0;
  let offsetY = 0;

  if (imageRatio > targetRatio) {
    drawHeight = width / imageRatio;
    offsetY = (height - drawHeight) / 2;
  } else {
    drawWidth = height * imageRatio;
    offsetX = (width - drawWidth) / 2;
  }

  try {
    ctx.drawImage(image, x + offsetX, y + offsetY, drawWidth, drawHeight);
    return true;
  } catch (_drawError) {
    return false;
  }
}

function parseServiceTimeMinutes(time = "") {
  const match = String(time).match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return hours * 60 + minutes;
}

function formatServiceTime(time = "") {
  const minutes = parseServiceTimeMinutes(time);
  if (minutes === null) return "";
  const date = new Date();
  date.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
  return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function getNextOrgServiceOccurrence(serviceTimes = []) {
  const now = new Date();
  const candidates = (Array.isArray(serviceTimes) ? serviceTimes : [])
    .map((serviceTime) => {
      const minutes = parseServiceTimeMinutes(serviceTime.time);
      if (minutes === null) return null;

      const startsAt = new Date(now);
      startsAt.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
      if (startsAt.getTime() < now.getTime() - 5 * 60 * 1000) {
        startsAt.setDate(startsAt.getDate() + 1);
      }

      return {
        startsAt: startsAt.toISOString(),
        label: serviceTime.name || "Service",
        timeLabel: formatServiceTime(serviceTime.time),
      };
    })
    .filter(Boolean)
    .sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime());

  return candidates[0] || null;
}

function getCountdownTarget(activeEvent, draft, serviceTimes) {
  const scheduledStartTime = activeEvent?.scheduledStartTime || activeEvent?.scheduled_start_time || draft?.scheduledStartTime || "";
  const scheduledMs = scheduledStartTime ? new Date(scheduledStartTime).getTime() : 0;

  if (scheduledMs && !Number.isNaN(scheduledMs) && scheduledMs >= Date.now() - 5 * 60 * 1000) {
    return {
      startsAt: new Date(scheduledMs).toISOString(),
      label: activeEvent?.title || draft?.title || "Scheduled service",
      timeLabel: new Date(scheduledMs).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }),
    };
  }

  return getNextOrgServiceOccurrence(serviceTimes);
}

function drawCountdown(ctx, width, height, workspaceName, countdownTarget, logoImage) {
  const startTime = countdownTarget?.startsAt ? new Date(countdownTarget.startsAt).getTime() : 0;
  const remainingMs = Math.max(0, startTime - Date.now());
  const totalSeconds = Math.ceil(remainingMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const timeText = startTime
    ? [hours, minutes, seconds].map((value) => String(value).padStart(2, "0")).join(":")
    : "Soon";

  const background = ctx.createLinearGradient(0, 0, width, height);
  background.addColorStop(0, "#eff6ff");
  background.addColorStop(0.42, "#ffffff");
  background.addColorStop(1, "#f6f8f4");
  ctx.fillStyle = background;
  ctx.fillRect(0, 0, width, height);

  const glow = ctx.createRadialGradient(width * 0.5, height * 0.34, 0, width * 0.5, height * 0.34, width * 0.56);
  glow.addColorStop(0, "rgba(37, 99, 235, 0.2)");
  glow.addColorStop(0.48, "rgba(191, 219, 254, 0.18)");
  glow.addColorStop(1, "rgba(191, 219, 254, 0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, width, height);

  const logoBoxWidth = Math.min(width * 0.46, 430);
  const logoBoxHeight = Math.min(height * 0.24, 220);
  const logoX = width / 2 - logoBoxWidth / 2;
  const logoY = height * 0.09;
  ctx.save();
  ctx.shadowColor = "rgba(37, 99, 235, 0.22)";
  ctx.shadowBlur = 34;
  ctx.shadowOffsetY = 18;
  drawImageContain(ctx, logoImage, logoX, logoY, logoBoxWidth, logoBoxHeight);
  ctx.restore();

  ctx.textAlign = "center";
  ctx.fillStyle = "#1d4ed8";
  ctx.font = "900 34px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  ctx.fillText(countdownTarget?.label || "Next Service", width / 2, height * 0.38);
  ctx.fillStyle = "#17251e";
  ctx.font = "800 56px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  ctx.fillText(workspaceName, width / 2, height * 0.47);
  ctx.fillStyle = "#0f172a";
  ctx.font = "900 150px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  ctx.fillText(timeText, width / 2, height * 0.67);
  ctx.font = "800 34px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  ctx.fillStyle = "#475569";
  const detailText = startTime && remainingMs <= 0
    ? "Service is starting"
    : countdownTarget?.timeLabel
      ? `Service begins at ${countdownTarget.timeLabel}`
      : "Service begins soon";
  ctx.fillText(detailText, width / 2, height * 0.78);
}

function StatusPill({ tone = "neutral", children }) {
  return <StreamTag tone={tone}>{children}</StreamTag>;
}

function StreamTag({ tone = "neutral", active = false, warning = false, children }) {
  const ready = active || tone === "neutral" || tone === "good" || tone === "ready";
  const caution = warning || tone === "warning";
  return (
    <span style={{ ...styles.encoderTag, ...(ready ? styles.encoderTagReady : {}), ...(caution ? styles.encoderTagWarning : {}), ...(tone === "danger" ? styles.encoderTagDanger : {}) }}>
      <span style={{ ...styles.encoderStatusDot, ...(ready ? styles.encoderStatusDotGood : {}), ...(caution ? styles.encoderStatusDotWarning : {}), ...(tone === "danger" ? styles.encoderStatusDotDanger : {}) }} />
      <span style={styles.encoderTagText}>{children}</span>
    </span>
  );
}

function streamEventLabel(event = {}) {
  const title = event.title || "Untitled stream";
  const start = event.scheduledStartTime || event.scheduled_start_time;
  if (!start) return title;
  const date = new Date(start);
  if (Number.isNaN(date.getTime())) return title;
  return `${title} - ${date.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}`;
}

function streamEventMenuMeta(event = {}) {
  const title = event.title || "Untitled stream";
  return streamEventLabel(event).replace(`${title} - `, "");
}

function getStreamEventStartTime(event = {}) {
  const value = event.scheduledStartTime || event.scheduled_start_time || event.scheduled_start || "";
  const time = value ? new Date(value).getTime() : 0;
  return Number.isNaN(time) ? 0 : time;
}

function isUpcomingStreamEvent(event = {}) {
  const status = String(event.status || "").toLowerCase();
  if (["live", "ended", "complete", "completed"].includes(status)) return false;
  const startTime = getStreamEventStartTime(event);
  if (!startTime) return true;
  return startTime >= Date.now() - 30 * 60 * 1000;
}

function isCompletedStreamEvent(event = {}) {
  const status = String(event.status || "").toLowerCase();
  if (["live", "ended", "complete", "completed"].includes(status)) return true;
  const startTime = getStreamEventStartTime(event);
  return Boolean(startTime && startTime < Date.now() - 30 * 60 * 1000);
}

function getCompletedStreamWarning() {
  return "This scheduled event has already run. Please schedule a new event.";
}

function isRedundantTransitionError(error) {
  return String(error?.message || error || "").toLowerCase().includes("redundant transition");
}

function sortUpcomingStreamEvents(events = []) {
  return [...events]
    .filter(isUpcomingStreamEvent)
    .sort((a, b) => {
      const aTime = getStreamEventStartTime(a) || Number.MAX_SAFE_INTEGER;
      const bTime = getStreamEventStartTime(b) || Number.MAX_SAFE_INTEGER;
      return aTime - bTime;
    });
}

function SectionCard({ title, icon: Icon, children, action }) {
  return (
    <section style={styles.card}>
      <div style={styles.cardHeader}>
        <div style={styles.cardTitleWrap}>
          {Icon ? <Icon size={18} /> : null}
          <h2 style={styles.cardTitle}>{title}</h2>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function SourceDot({ active, label }) {
  return <StreamTag active={active} warning={!active}>{label}</StreamTag>;
}

function isInsecureIngestUrl(ingestUrl = "") {
  return (
    typeof window !== "undefined" &&
    window.location.protocol === "https:" &&
    String(ingestUrl || "").startsWith("ws://")
  );
}

function wait(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

async function findOrganizationLiveDisplay(accountId = "", userId = "") {
  if (accountId) {
    const { data, error } = await supabase
      .from("church_live_displays")
      .select("id, public_code, updated_at")
      .eq("account_id", accountId)
      .eq("is_active", true)
      .order("updated_at", { ascending: false })
      .limit(1);

    if (error) throw error;
    if (data?.[0]?.public_code) return data[0];
  }

  if (userId) {
    const { data, error } = await supabase
      .from("church_live_displays")
      .select("id, public_code, updated_at")
      .eq("user_id", userId)
      .eq("mode", "church")
      .eq("is_active", true)
      .order("updated_at", { ascending: false })
      .limit(1);

    if (error) throw error;
    if (data?.[0]?.public_code) return data[0];
  }

  return null;
}

export default function StreamPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const { isPhone } = useResponsive();
  const canvasRef = useRef(null);
  const cameraVideoRef = useRef(null);
  const displayVideoRef = useRef(null);
  const animationRef = useRef(0);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const meterFrameRef = useRef(0);
  const cameraStreamRef = useRef(null);
  const displayStreamRef = useRef(null);
  const micStreamRef = useRef(null);
  const errorLogRef = useRef(null);
  const streamMenuRef = useRef(null);
  const countdownLogoImageRef = useRef(null);
  const lastNonCountdownLayoutRef = useRef("slides-camera");
  const streamControlChannelRef = useRef(null);
  const relaySocketRef = useRef(null);
  const relayRecorderRef = useRef(null);
  const relayAudioContextRef = useRef(null);

  const [devices, setDevices] = useState({ cameras: [], microphones: [] });
  const [selectedCameraId, setSelectedCameraId] = useState("");
  const [selectedMicId, setSelectedMicId] = useState("");
  const [cameraStream, setCameraStream] = useState(null);
  const [displayStream, setDisplayStream] = useState(null);
  const [micStream, setMicStream] = useState(null);
  const [layoutId, setLayoutId] = useState("slides-camera");
  const [previewActive, setPreviewActive] = useState(false);
  const [streamStatus, setStreamStatus] = useState("Draft");
  const [notice, setNotice] = useState("");
  const [warning, setWarning] = useState("");
  const [error, setError] = useState("");
  const [audioLevel, setAudioLevel] = useState(0);
  const [draft, setDraft] = useState(DEFAULT_STREAM_DRAFT);
  const [connection, setConnection] = useState(null);
  const [streamEvents, setStreamEvents] = useState([]);
  const [activeEvent, setActiveEvent] = useState(null);
  const [resolvedAccountId, setResolvedAccountId] = useState("");
  const [streamDestination, setStreamDestination] = useState(null);
  const [destinationDraft, setDestinationDraft] = useState({ rtmpUrl: DEFAULT_YOUTUBE_RTMP_URL, streamKey: "" });
  const [destinationBusy, setDestinationBusy] = useState(false);
  const [relayConfigured, setRelayConfigured] = useState(false);
  const [relayEncoderTarget, setRelayEncoderTarget] = useState("Server encoder");
  const [relayStreaming, setRelayStreaming] = useState(false);
  const [youtubeBusy, setYoutubeBusy] = useState(false);
  const [streamAppIconUrl, setStreamAppIconUrl] = useState(STREAM_APP_ICON_URL);
  const [orgAccount, setOrgAccount] = useState(null);
  const [serviceTimes, setServiceTimes] = useState([]);
  const [countdownLogoReady, setCountdownLogoReady] = useState(0);
  const [preLiveCountdown, setPreLiveCountdown] = useState(null);
  const [errorLog, setErrorLog] = useState([]);
  const [showErrorLog, setShowErrorLog] = useState(false);
  const [showStreamMenu, setShowStreamMenu] = useState(false);
  const [health, setHealth] = useState({
    browser: "Idle",
    relay: "Not connected",
    youtube: "Not connected",
    fps: 30,
    bitrate: "0 kbps",
    droppedFrames: 0,
  });

  const account = getChurchStreamAccount(user);
  const accountId = resolvedAccountId || account?.id || params.orgId || "";
  const workspaceName = orgAccount?.name || getWorkspaceName(user);
  const logoUrl = streamAppIconUrl || getWorkspaceLogo(user);
  const orgLogoUrl = orgAccount?.logo_url || orgAccount?.logoUrl || getOrganizationLogo(user);
  const youtubeConnected = connection?.status === "connected";
  const relayReady = relayConfigured;
  const encoderOnlyMode = !youtubeConnected;
  const canCreateStream = youtubeConnected || encoderOnlyMode;
  const selectedLayout = SCENE_LAYOUTS.find((layout) => layout.id === layoutId) || SCENE_LAYOUTS[0];
  const mediaSupported = Boolean(navigator.mediaDevices?.getUserMedia);
  const cameraConnected = Boolean(cameraStream?.getVideoTracks?.().some((track) => track.readyState === "live"));
  const micConnected = Boolean(micStream?.getAudioTracks?.().some((track) => track.readyState === "live"));
  const displayConnected = Boolean(displayStream?.getVideoTracks?.().some((track) => track.readyState === "live"));
  const upcomingStreamEvents = useMemo(() => sortUpcomingStreamEvents(streamEvents), [streamEvents]);
  const countdownTarget = useMemo(() => getCountdownTarget(activeEvent, draft, serviceTimes), [activeEvent, draft, serviceTimes]);

  function postStreamControlStatus() {
    streamControlChannelRef.current?.postMessage?.({
      type: "status",
      status: streamStatus,
      live: relayStreaming || streamStatus === "Live",
      ready: true,
      activeEventId: activeEvent?.id || "",
      activeEventTitle: activeEvent?.title || "",
      events: upcomingStreamEvents.map((event) => ({
        id: event.id,
        title: event.title || "Untitled stream",
        meta: streamEventMenuMeta(event),
        status: event.status || "",
      })),
      updatedAt: Date.now(),
    });
  }

  useEffect(() => {
    if (!error) return;
    const entry = {
      id: `${Date.now()}-${Math.random()}`,
      message: error,
      time: new Date().toLocaleTimeString(),
    };
    setErrorLog((current) => [entry, ...current].slice(0, 8));
    setShowErrorLog(true);
  }, [error]);

  useEffect(() => {
    if (!showErrorLog) return undefined;

    function closeOnOutsideClick(event) {
      if (errorLogRef.current?.contains(event.target)) return;
      setShowErrorLog(false);
    }

    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, [showErrorLog]);

  useEffect(() => {
    if (!showStreamMenu) return undefined;

    function closeOnOutsideClick(event) {
      if (streamMenuRef.current?.contains(event.target)) return;
      setShowStreamMenu(false);
    }

    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, [showStreamMenu]);

  useEffect(() => {
    if (layoutId !== "countdown") {
      lastNonCountdownLayoutRef.current = layoutId;
    }
  }, [layoutId]);

  useEffect(() => {
    const tracks = micStream?.getAudioTracks?.() || [];
    if (!tracks.length) return undefined;

    if (layoutId === "countdown") {
      tracks.forEach((track) => {
        track.enabled = false;
      });
      setHealth((current) => ({ ...current, browser: "Countdown muted" }));
      return () => {
        tracks.forEach((track) => {
          track.enabled = true;
        });
      };
    }

    tracks.forEach((track) => {
      track.enabled = true;
    });
    return undefined;
  }, [layoutId, micStream]);

  useEffect(() => {
    if (layoutId !== "countdown" || !countdownTarget?.startsAt) return undefined;

    const targetMs = new Date(countdownTarget.startsAt).getTime();
    if (!targetMs || Number.isNaN(targetMs)) return undefined;

    const delay = Math.max(0, targetMs - Date.now());
    const timeoutId = window.setTimeout(() => {
      setLayoutId(lastNonCountdownLayoutRef.current || "slides-camera");
    }, delay + 600);

    return () => window.clearTimeout(timeoutId);
  }, [countdownTarget?.startsAt, layoutId]);

  useEffect(() => {
    if (typeof window === "undefined" || !window.BroadcastChannel) return undefined;

    const channel = new window.BroadcastChannel(STREAM_CONTROL_CHANNEL);
    streamControlChannelRef.current = channel;
    channel.onmessage = (event) => {
      const message = event.data || {};
      if (message.type === "get-status") {
        postStreamControlStatus();
        return;
      }
      if (message.type !== "command") return;
      if (message.eventId) {
        const requestedEvent = upcomingStreamEvents.find((event) => event.id === message.eventId);
        if (requestedEvent) {
          setActiveEvent(requestedEvent);
          setStreamStatus(requestedEvent.status === "live" ? "Live" : "Ready");
        }
      }
      if (message.action === "go-live") {
        requestGoLive();
      } else if (message.action === "end-live") {
        endStream();
      }
    };
    postStreamControlStatus();

    return () => {
      channel.close();
      streamControlChannelRef.current = null;
    };
  }, [activeEvent?.id, relayStreaming, streamStatus, upcomingStreamEvents]);

  useEffect(() => {
    postStreamControlStatus();
  }, [activeEvent?.id, relayStreaming, streamStatus, upcomingStreamEvents]);

  const missingWarnings = useMemo(() => {
    const warnings = [];
    if (!micStream) warnings.push("No microphone selected.");
    if (!displayStream && layoutId.includes("slides")) warnings.push("No Oikos display source selected.");
    if (!cameraStream && layoutId.includes("camera")) warnings.push("No camera source selected.");
    return warnings;
  }, [cameraStream, displayStream, layoutId, micStream]);

  useEffect(() => {
    async function loadDevices() {
      if (!navigator.mediaDevices?.enumerateDevices) return;

      try {
        const nextDevices = await navigator.mediaDevices.enumerateDevices();
        const cameras = nextDevices.filter((device) => device.kind === "videoinput");
        const microphones = nextDevices.filter((device) => device.kind === "audioinput");
        setDevices({ cameras, microphones });
        setSelectedCameraId((current) => current || cameras[0]?.deviceId || "");
        setSelectedMicId((current) => current || microphones[0]?.deviceId || "");
      } catch (_loadError) {
        setError("Device list is unavailable until browser media permissions are granted.");
      }
    }

    loadDevices();
  }, []);

  useEffect(() => {
    if (searchParams.get("youtube") === "connected") {
      setNotice("YouTube is connected. You can create a broadcast now.");
    }
  }, [searchParams]);

  useEffect(() => {
    let mounted = true;

    async function loadStreamAppIcon() {
      const { data, error } = await supabase
        .from("tile_store_apps")
        .select("icon_url, logo_url")
        .eq("tile_id", "church-stream")
        .maybeSingle();

      if (!mounted || error) return;
      setStreamAppIconUrl(data?.icon_url || STREAM_APP_ICON_URL);
    }

    loadStreamAppIcon();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    async function loadOrgAccount() {
      if (!accountId) {
        setOrgAccount(null);
        return;
      }

      const { data, error } = await supabase
        .from("accounts")
        .select("id, name, logo_url")
        .eq("id", accountId)
        .maybeSingle();

      if (!mounted || error) return;
      setOrgAccount(data || null);
    }

    loadOrgAccount();
    return () => {
      mounted = false;
    };
  }, [accountId]);

  useEffect(() => {
    let mounted = true;

    async function loadOrgServiceTimes() {
      if (!accountId) {
        setServiceTimes([]);
        return;
      }

      try {
        const nextServiceTimes = await loadChurchServiceTimes(accountId);
        if (mounted) setServiceTimes(nextServiceTimes);
      } catch (serviceTimesError) {
        if (mounted) {
          setServiceTimes([]);
          setError(serviceTimesError?.message || "Could not load this organization's service times.");
        }
      }
    }

    loadOrgServiceTimes();
    return () => {
      mounted = false;
    };
  }, [accountId]);

  useEffect(() => {
    if (!orgLogoUrl) {
      countdownLogoImageRef.current = null;
      setCountdownLogoReady((current) => current + 1);
      return undefined;
    }

    let cancelled = false;
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => {
      if (cancelled) return;
      countdownLogoImageRef.current = image;
      setCountdownLogoReady((current) => current + 1);
    };
    image.onerror = () => {
      if (cancelled) return;
      countdownLogoImageRef.current = null;
      setCountdownLogoReady((current) => current + 1);
    };
    image.src = orgLogoUrl;

    return () => {
      cancelled = true;
    };
  }, [orgLogoUrl]);

  useEffect(() => {
    let mounted = true;

    async function resolveAccountId() {
      if (!user) return;
      const nextAccountId = await getChurchStreamAccountId(user, params.orgId || "");
      if (mounted) setResolvedAccountId(nextAccountId || "");
    }

    resolveAccountId();
    return () => {
      mounted = false;
    };
  }, [params.orgId, user]);

  useEffect(() => {
    let mounted = true;

    async function loadWorkspace() {
      if (!accountId || !user) return;

      try {
        const workspace = await loadChurchStreamWorkspace(user, accountId);
        if (!mounted) return;
        setConnection(workspace.connection || null);
        setStreamDestination(workspace.streamDestination || null);
        setDestinationDraft((current) => ({
          ...current,
          rtmpUrl: workspace.streamDestination?.rtmpUrl || current.rtmpUrl || DEFAULT_YOUTUBE_RTMP_URL,
        }));
        const nextEvents = workspace.events || [];
        const nextUpcomingEvents = sortUpcomingStreamEvents(nextEvents);
        setStreamEvents(nextEvents);
        setActiveEvent((current) => {
          if (current?.id && nextUpcomingEvents.some((event) => event.id === current.id)) return current;
          return nextUpcomingEvents[0] || null;
        });
        setRelayConfigured(Boolean(workspace.relay?.configured));
        setRelayEncoderTarget(workspace.relay?.encoderTarget || "Server encoder");
        setHealth((current) => ({
          ...current,
          relay: workspace.relay?.configured ? workspace.relay?.encoderTarget || "Configured" : "Relay endpoint needed",
          youtube: workspace.connection?.status === "connected" ? "Connected" : "Not connected",
        }));
      } catch (workspaceError) {
        if (mounted) {
          setHealth((current) => ({ ...current, youtube: "Needs setup" }));
          setError(workspaceError?.message || "Could not load YouTube stream workspace.");
        }
      }
    }

    loadWorkspace();
    return () => {
      mounted = false;
    };
  }, [accountId, user]);

  useEffect(() => {
    cameraStreamRef.current = cameraStream;
    if (cameraVideoRef.current) {
      cameraVideoRef.current.srcObject = cameraStream;
    }
  }, [cameraStream]);

  useEffect(() => {
    displayStreamRef.current = displayStream;
    if (displayVideoRef.current) {
      displayVideoRef.current.srcObject = displayStream;
    }
  }, [displayStream]);

  useEffect(() => {
    micStreamRef.current = micStream;
  }, [micStream]);

  useEffect(() => {
    return () => {
      window.cancelAnimationFrame(animationRef.current);
      window.cancelAnimationFrame(meterFrameRef.current);
      stopStream(cameraStreamRef.current);
      stopStream(displayStreamRef.current);
      stopStream(micStreamRef.current);
      stopRelayStreaming();
      audioContextRef.current?.close?.();
    };
  }, []);

  useEffect(() => {
    if (!previewActive) {
      window.cancelAnimationFrame(animationRef.current);
      return undefined;
    }

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return undefined;

    const render = () => {
      const width = canvas.width;
      const height = canvas.height;
      ctx.fillStyle = "#0f1b15";
      ctx.fillRect(0, 0, width, height);

      if (layoutId === "countdown") {
        drawCountdown(ctx, width, height, workspaceName, countdownTarget, countdownLogoImageRef.current);
      } else if (layoutId === "camera") {
        if (!drawVideoCover(ctx, cameraVideoRef.current, 0, 0, width, height)) {
          drawPlaceholder(ctx, width, height, "Camera", "Select a camera source");
        }
      } else if (layoutId === "camera-slides") {
        if (!drawVideoCover(ctx, cameraVideoRef.current, 0, 0, width, height)) {
          drawPlaceholder(ctx, width, height, "Camera", "Main camera source");
        }
        const insetWidth = width * 0.34;
        const insetHeight = insetWidth * 0.5625;
        ctx.fillStyle = "#000000";
        ctx.fillRect(width - insetWidth - 44, 44, insetWidth, insetHeight);
        drawVideoContain(ctx, displayVideoRef.current, width - insetWidth - 44, 44, insetWidth, insetHeight);
      } else if (layoutId === "slides-camera") {
        if (!drawVideoContainWithSoftFill(ctx, displayVideoRef.current, 0, 0, width, height)) {
          drawPlaceholder(ctx, width, height, workspaceName, "Capture the Oikos Live Display tab");
        }
        const insetWidth = width * 0.26;
        const insetHeight = insetWidth * 0.5625;
        const insetX = width - insetWidth - 44;
        const insetY = height - insetHeight - 44;
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(insetX - 8, insetY - 8, insetWidth + 16, insetHeight + 16);
        if (!drawVideoCover(ctx, cameraVideoRef.current, insetX, insetY, insetWidth, insetHeight)) {
          drawPanelPlaceholder(ctx, insetX, insetY, insetWidth, insetHeight, "Camera", "Select camera");
        }
      } else {
        if (!drawVideoContainWithSoftFill(ctx, displayVideoRef.current, 0, 0, width, height)) {
          const title = layoutId === "announcements" ? "Announcements" : workspaceName;
          drawPlaceholder(ctx, width, height, title, "Capture the Oikos Live Display tab");
        }
      }

      animationRef.current = window.requestAnimationFrame(render);
    };

    render();
    return () => window.cancelAnimationFrame(animationRef.current);
  }, [countdownLogoReady, countdownTarget, layoutId, previewActive, workspaceName]);

  async function refreshDevices() {
    try {
      if (!navigator.mediaDevices?.enumerateDevices) {
        setError("Device list is not available in this browser.");
        return;
      }

      const nextDevices = await navigator.mediaDevices.enumerateDevices();
      setDevices({
        cameras: nextDevices.filter((device) => device.kind === "videoinput"),
        microphones: nextDevices.filter((device) => device.kind === "audioinput"),
      });
      setNotice("Device list refreshed.");
    } catch (_refreshError) {
      setError("Could not refresh devices. Check browser permissions.");
    }
  }

  async function startCamera() {
    setError("");
    try {
      if (!mediaSupported) {
        setError("Camera access requires a supported browser on localhost or HTTPS.");
        return;
      }

      stopStream(cameraStream);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: selectedCameraId ? { deviceId: { exact: selectedCameraId } } : true,
        audio: false,
      });
      stream.getVideoTracks().forEach((track) => {
        track.addEventListener("ended", () => {
          setCameraStream(null);
          setNotice("Camera source disconnected.");
        });
      });
      setCameraStream(stream);
      setHealth((current) => ({ ...current, browser: "Camera connected" }));
      setNotice("Camera source is ready.");
      refreshDevices().catch(() => {});
    } catch (cameraError) {
      setError(getMediaErrorMessage(cameraError, "Camera"));
    }
  }

  async function startMicrophone() {
    setError("");
    try {
      if (!mediaSupported) {
        setError("Microphone access requires a supported browser on localhost or HTTPS.");
        return;
      }

      stopStream(micStream);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: false,
        audio: selectedMicId ? { deviceId: { exact: selectedMicId } } : true,
      });
      stream.getAudioTracks().forEach((track) => {
        track.addEventListener("ended", () => {
          setMicStream(null);
          setAudioLevel(0);
          setNotice("Microphone source disconnected.");
        });
      });
      setMicStream(stream);
      startAudioMeter(stream);
      setHealth((current) => ({ ...current, browser: "Microphone connected" }));
      setNotice("Microphone source is ready.");
      refreshDevices().catch(() => {});
    } catch (micError) {
      setError(getMediaErrorMessage(micError, "Microphone"));
    }
  }

  async function connectCameraAndMicrophone() {
    await Promise.all([startCamera(), startMicrophone()]);
  }

  async function captureDisplay() {
    setError("");
    try {
      stopStream(displayStream);
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: 30 },
        audio: true,
      });
      stream.getVideoTracks().forEach((track) => {
        track.addEventListener("ended", () => {
          setDisplayStream(null);
          setNotice("Display source disconnected.");
        });
      });
      setDisplayStream(stream);
      setNotice("Display source captured. Select the Oikos Live Display tab for best results.");
    } catch (_displayError) {
      setError("Display capture was cancelled or blocked by the browser.");
    }
  }

  function startAudioMeter(stream) {
    window.cancelAnimationFrame(meterFrameRef.current);
    audioContextRef.current?.close?.();
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;

    const audioContext = new AudioContextClass();
    const analyser = audioContext.createAnalyser();
    const source = audioContext.createMediaStreamSource(stream);
    source.connect(analyser);
    analyser.fftSize = 256;
    audioContextRef.current = audioContext;
    analyserRef.current = analyser;

    const data = new Uint8Array(analyser.frequencyBinCount);
    const tick = () => {
      analyser.getByteFrequencyData(data);
      const average = data.reduce((sum, value) => sum + value, 0) / Math.max(data.length, 1);
      setAudioLevel(Math.min(100, Math.round((average / 128) * 100)));
      meterFrameRef.current = window.requestAnimationFrame(tick);
    };
    tick();
  }

  function startPreview() {
    setPreviewActive(true);
    setStreamStatus("Preview");
    setHealth((current) => ({ ...current, browser: "Previewing", fps: 30, bitrate: "Local preview" }));
  }

  function createRelayMediaStream() {
    const canvas = canvasRef.current;
    if (!canvas?.captureStream) {
      throw new Error("This browser cannot capture the preview canvas for streaming.");
    }

    const outputStream = new MediaStream();
    canvas.captureStream(30).getVideoTracks().forEach((track) => outputStream.addTrack(track));

    const audioSources = [
      micStreamRef.current,
      displayStreamRef.current,
    ].filter((stream) => stream?.getAudioTracks?.().some((track) => track.readyState === "live"));

    if (audioSources.length) {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (AudioContextClass) {
        relayAudioContextRef.current?.close?.();
        const audioContext = new AudioContextClass();
        const destination = audioContext.createMediaStreamDestination();
        audioSources.forEach((stream) => {
          const source = audioContext.createMediaStreamSource(stream);
          source.connect(destination);
        });
        relayAudioContextRef.current = audioContext;
        destination.stream.getAudioTracks().forEach((track) => outputStream.addTrack(track));
      }
    }

    return outputStream;
  }

  function stopRelayStreaming() {
    if (relayRecorderRef.current?.state && relayRecorderRef.current.state !== "inactive") {
      relayRecorderRef.current.stop();
    }
    relayRecorderRef.current = null;

    if (relaySocketRef.current?.readyState === WebSocket.OPEN || relaySocketRef.current?.readyState === WebSocket.CONNECTING) {
      relaySocketRef.current.close();
    }
    relaySocketRef.current = null;
    relayAudioContextRef.current?.close?.();
    relayAudioContextRef.current = null;
    setRelayStreaming(false);
  }

  async function startRelayStreaming(ingestUrl) {
    if (!ingestUrl) {
      throw new Error("The relay did not return an ingest URL. Create a new stream after the server relay is configured.");
    }
    if (!window.WebSocket || !window.MediaRecorder) {
      throw new Error("This browser cannot stream to the relay. Use a current Chrome, Edge, or Firefox build.");
    }
    if (!previewActive) {
      setPreviewActive(true);
    }

    const mimeType = getRecorderMimeType();
    if (!mimeType) {
      throw new Error("This browser does not support WebM recording for the relay.");
    }

    const relayStream = createRelayMediaStream();
    const websocket = new WebSocket(ingestUrl);
    websocket.binaryType = "arraybuffer";
    relaySocketRef.current = websocket;

    await new Promise((resolve, reject) => {
      const timeout = window.setTimeout(() => reject(new Error("Relay connection timed out.")), 10000);
      websocket.onopen = () => {
        window.clearTimeout(timeout);
        resolve();
      };
      websocket.onerror = () => {
        window.clearTimeout(timeout);
        reject(new Error("Could not connect to the stream relay."));
      };
    });

    const recorder = new MediaRecorder(relayStream, {
      mimeType,
      videoBitsPerSecond: 4500000,
      audioBitsPerSecond: 160000,
    });
    relayRecorderRef.current = recorder;

    recorder.ondataavailable = async (event) => {
      if (!event.data?.size || websocket.readyState !== WebSocket.OPEN) return;
      websocket.send(await event.data.arrayBuffer());
    };
    recorder.onerror = () => {
      setError("The browser encoder stopped unexpectedly.");
      stopRelayStreaming();
    };
    websocket.onclose = () => {
      if (relayRecorderRef.current?.state === "recording") {
        setError("Relay connection closed.");
      }
      stopRelayStreaming();
    };

    recorder.start(1000);
    setRelayStreaming(true);
    setHealth((current) => ({ ...current, relay: "Streaming", bitrate: "4500 kbps" }));
  }

  async function connectYouTube() {
    setError("");
    setYoutubeBusy(true);
    try {
      await startChurchYouTubeOAuth(user, accountId);
    } catch (oauthError) {
      setError(oauthError?.message || "Could not start YouTube OAuth.");
      setYoutubeBusy(false);
    }
  }

  async function createStream() {
    setError("");
    setYoutubeBusy(true);
    try {
      const payload = await createChurchYouTubeBroadcast(user, draft, accountId);
      setActiveEvent(payload.event || null);
      setStreamEvents((current) => sortUpcomingStreamEvents([payload.event, ...current.filter((item) => item.id !== payload.event?.id)].filter(Boolean)));
      setStreamStatus("Ready");
      setRelayConfigured(Boolean(payload.event?.relay?.ingestUrl) || relayConfigured);
      setRelayEncoderTarget(payload.event?.relay?.encoderTarget || relayEncoderTarget);
      setHealth((current) => ({
        ...current,
        relay: payload.event?.relay?.ingestUrl ? payload.event?.relay?.encoderTarget || "Ready" : current.relay,
        youtube: "Broadcast ready",
      }));
      setNotice(payload.event?.youtubeWatchUrl
        ? `YouTube broadcast and ${payload.event?.relay?.encoderTarget || "server encoder"} are ready.`
        : `${payload.event?.relay?.encoderTarget || "Server encoder"} session is ready.`);
      return payload.event || null;
    } catch (createError) {
      setError(createError?.message || "Could not create the stream session.");
      return null;
    } finally {
      setYoutubeBusy(false);
    }
  }

  async function saveDestination() {
    setError("");
    if (!destinationDraft.streamKey.trim()) {
      setError("Paste a YouTube stream key before saving.");
      return;
    }
    if (!accountId) {
      setError("Sign in to a church organization before saving a stream key.");
      return;
    }
    setDestinationBusy(true);
    try {
      const payload = await saveChurchStreamDestination(user, destinationDraft, accountId);
      setStreamDestination(payload.streamDestination || null);
      setDestinationDraft((current) => ({ ...current, streamKey: "" }));
      setNotice("YouTube stream key saved for this organization.");
    } catch (destinationError) {
      setError(destinationError?.message || "Could not save the YouTube stream key.");
    } finally {
      setDestinationBusy(false);
    }
  }

  async function clearDestination() {
    setError("");
    if (!accountId) {
      setError("Sign in to a church organization before clearing a stream key.");
      return;
    }
    setDestinationBusy(true);
    try {
      const payload = await clearChurchStreamDestination(user, accountId);
      setStreamDestination(payload.streamDestination || null);
      setDestinationDraft({ rtmpUrl: DEFAULT_YOUTUBE_RTMP_URL, streamKey: "" });
      setNotice("YouTube stream key cleared.");
    } catch (destinationError) {
      setError(destinationError?.message || "Could not clear the YouTube stream key.");
    } finally {
      setDestinationBusy(false);
    }
  }

  async function requestGoLive() {
    let eventToStream = activeEvent;

    setWarning("");

    if (!eventToStream?.id) {
      setError("Select a scheduled stream before going live.");
      return;
    }

    if (isCompletedStreamEvent(eventToStream)) {
      setError("");
      setWarning(getCompletedStreamWarning());
      return;
    }

    if (isInsecureIngestUrl(eventToStream?.relay?.ingestUrl)) {
      setError("This scheduled stream has an old insecure relay session. Create another stream, then select it before going live.");
      return;
    }

    if (!eventToStream?.relay?.ingestUrl) {
      setError("This stream does not have a relay ingest URL. Create a stream session before going live.");
      return;
    }

    setError("");
    setYoutubeBusy(true);
    try {
      await runPreLiveCountdown();
      await startRelayStreaming(eventToStream.relay.ingestUrl);
      if (eventToStream.youtubeWatchUrl) {
        setNotice("Stream is sending. Waiting for YouTube to detect the signal...");
        await wait(8000);
        try {
          await transitionYouTubeWithRetry(eventToStream.id, "testing", 3);
        } catch (_testingError) {
          // Some broadcasts cannot enter testing once YouTube has enough signal; try live directly.
        }
        let payload = null;
        try {
          payload = await transitionYouTubeWithRetry(eventToStream.id, "live", 5);
        } catch (transitionError) {
          if (!isRedundantTransitionError(transitionError)) throw transitionError;
          payload = { event: { ...eventToStream, status: "live" } };
        }
        setActiveEvent((payload.event || eventToStream));
      } else {
        setActiveEvent({ ...eventToStream, status: "live" });
      }
      setStreamStatus("Live");
      setHealth((current) => ({ ...current, youtube: "Live" }));
      setNotice(eventToStream.youtubeWatchUrl ? "YouTube broadcast is live." : "Oikos Relay Server encoder stream is live.");
    } catch (liveError) {
      const liveMessage = liveError?.message || "Could not start the live stream.";
      if (isRedundantTransitionError(liveError)) {
        setNotice("YouTube already has this broadcast in that live state.");
      } else if (liveMessage === "Could not connect to the stream relay." && isCompletedStreamEvent(eventToStream)) {
        setWarning(getCompletedStreamWarning());
      } else {
        setError(liveMessage);
      }
    } finally {
      setPreLiveCountdown(null);
      setYoutubeBusy(false);
    }
  }

  async function runPreLiveCountdown(seconds = 5) {
    for (let value = seconds; value > 0; value -= 1) {
      setPreLiveCountdown(value);
      await wait(1000);
    }
    setPreLiveCountdown(null);
  }

  async function transitionYouTubeWithRetry(eventId, transition, attempts = 4) {
    let lastError = null;

    for (let attempt = 1; attempt <= attempts; attempt += 1) {
      try {
        return await transitionChurchYouTubeBroadcast(user, eventId, transition, accountId);
      } catch (transitionError) {
        lastError = transitionError;
        if (attempt >= attempts) break;
        await wait(5000);
      }
    }

    throw lastError;
  }

  async function endStream() {
    stopRelayStreaming();

    if (activeEvent?.id && youtubeConnected) {
      try {
        const payload = await transitionChurchYouTubeBroadcast(user, activeEvent.id, "complete", accountId);
        setActiveEvent(payload.event || activeEvent);
      } catch (endError) {
        setError(endError?.message || "Could not complete the YouTube broadcast.");
      }
    }

    setStreamStatus("Ended");
    setPreviewActive(false);
    setHealth((current) => ({ ...current, browser: "Stopped", relay: relayReady ? relayEncoderTarget : "Stopped", youtube: activeEvent ? "Ended" : "Not connected", bitrate: "0 kbps" }));
  }

  async function openLiveDisplaySource() {
    setError("");

    try {
      const display = await findOrganizationLiveDisplay(accountId, user?.isChurchOrgUser ? "" : user?.id);

      if (!display?.public_code) {
        setError("No active Live Display was found for this organization. Open Live Display once to create the org display, then come back to Stream.");
        return;
      }

      window.open(`/live/${encodeURIComponent(display.public_code)}?controllerPreview=1`, "_blank", "noopener,noreferrer");
      setNotice("Opened this organization's Live Display. Use Capture and select that tab.");
    } catch (displayError) {
      setError(displayError?.message || "Could not open this organization's Live Display.");
    }
  }

  return (
    <main className="stream-studio-page" style={{ ...styles.page, ...(isPhone ? styles.pagePhone : {}) }}>
      <style>{STREAM_STUDIO_IDLE_CSS}</style>
      {notice ? <GlobalNotificationPill tone="success" message={notice} onClose={() => setNotice("")} /> : null}
      {warning ? <GlobalNotificationPill tone="warning" message={warning} onClose={() => setWarning("")} /> : null}
      {error ? <GlobalNotificationPill tone="error" message={error} onClose={() => setError("")} /> : null}
      {preLiveCountdown !== null ? (
        <div style={styles.preLiveOverlay} role="status" aria-live="assertive">
          <div style={styles.preLiveLockup}>
            <div className="stream-pre-live-logo" style={styles.preLiveLogoFrame}>
              <img src={logoUrl} alt="" style={styles.preLiveLogo} />
            </div>
            <div style={styles.preLiveCopy}>
              <span style={styles.preLiveEyebrow}>Going live in</span>
              <strong style={styles.preLiveCount}>{preLiveCountdown}</strong>
              <span style={styles.preLiveTitle}>Oikos Live Stream Studio</span>
            </div>
          </div>
        </div>
      ) : null}

      <section style={{ ...styles.studio, ...(isPhone ? styles.studioPhone : {}) }}>
        <header style={{ ...styles.topBar, ...(!isPhone ? styles.topBarEdge : {}), ...(isPhone ? styles.topBarPhone : {}) }}>
          <div style={styles.heroIdentity}>
            <button
              type="button"
              style={styles.logoButton}
              onClick={() => navigate("/church")}
              aria-label="Back to church apps"
              title="Back to church apps"
            >
              <img src={logoUrl} alt="" style={styles.logo} />
            </button>
            <div style={styles.heroCopy}>
              <p style={styles.eyebrow}>Oikos Live Stream Studio</p>
              <h1 style={styles.title}>{workspaceName}</h1>
            </div>
          </div>
          <div style={styles.statusRow}>
            <SourceDot active={cameraConnected} label="Camera" />
            <SourceDot active={micConnected} label="Mic" />
            <SourceDot active={displayConnected} label="Display" />
            <div ref={errorLogRef} style={styles.errorLogWrap}>
              <button
                type="button"
                style={{ ...styles.encoderTag, ...styles.encoderTagReady, ...styles.headerTagButton, ...(errorLog.length ? styles.encoderTagWarning : {}) }}
                onClick={() => setShowErrorLog((current) => !current)}
              >
                <span style={{ ...styles.encoderStatusDot, ...styles.encoderStatusDotGood, ...(errorLog.length ? styles.encoderStatusDotWarning : {}) }} />
                <span style={styles.encoderTagText}>Errors</span>
              </button>
              {showErrorLog ? (
                <div style={styles.errorLogPopover}>
                  <div style={styles.errorLogHeader}>
                    <strong>Stream Errors</strong>
                    <button type="button" style={styles.errorLogClearButton} onClick={() => setErrorLog([])}>Clear</button>
                  </div>
                  {errorLog.length ? (
                    <div style={styles.errorLogList}>
                      {errorLog.map((entry) => (
                        <div key={entry.id} style={styles.errorLogItem}>
                          <span style={styles.errorLogTime}>{entry.time}</span>
                          <pre style={styles.errorLogMessage}>{entry.message}</pre>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={styles.errorLogEmpty}>No errors yet.</p>
                  )}
                </div>
              ) : null}
            </div>
            <StatusPill tone={streamStatus === "Live" ? "danger" : streamStatus === "Ready" ? "good" : "neutral"}>{streamStatus}</StatusPill>
          </div>
        </header>

        <div style={{ ...styles.studioGrid, ...(!isPhone ? styles.studioGridEdge : {}), ...(isPhone ? styles.studioGridPhone : {}) }}>
          <aside style={{ ...styles.leftRail, ...(isPhone ? styles.leftRailPhone : {}) }}>
            <SectionCard title="Sources" icon={Video} action={<button type="button" style={styles.smallButton} onClick={refreshDevices}>Refresh</button>}>
              <div style={styles.cameraMonitor}>
                <video ref={cameraVideoRef} playsInline muted autoPlay style={styles.cameraMonitorVideo} />
                {!cameraConnected ? (
                  <div style={styles.cameraMonitorOverlay}>
                    <div style={styles.cameraIdleLockup}>
                      <span className="stream-idle-logo-frame" style={styles.cameraIdleLogoFrame}>
                        <img src={logoUrl} alt="" style={styles.cameraIdleLogo} />
                      </span>
                      <span style={styles.cameraIdleText}>Camera idle</span>
                    </div>
                  </div>
                ) : null}
              </div>

              <div style={styles.sourceControls}>
                <div style={styles.compactFields}>
                  <label style={styles.field}>
                    <span>Camera</span>
                    <select value={selectedCameraId} onChange={(event) => setSelectedCameraId(event.target.value)} style={styles.input}>
                      <option value="">Default camera</option>
                      {devices.cameras.map((device, index) => (
                        <option key={device.deviceId || index} value={device.deviceId}>
                          {device.label || `Camera ${index + 1}`}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label style={styles.field}>
                    <span>Microphone</span>
                    <select value={selectedMicId} onChange={(event) => setSelectedMicId(event.target.value)} style={styles.input}>
                      <option value="">Default microphone</option>
                      {devices.microphones.map((device, index) => (
                        <option key={device.deviceId || index} value={device.deviceId}>
                          {device.label || `Microphone ${index + 1}`}
                        </option>
                      ))}
                    </select>
                    <div style={styles.inlineAudioMeter}>
                      <span style={styles.inlineAudioLabel}>Mic level</span>
                      <div style={styles.audioTrack}><span style={{ ...styles.audioFill, width: `${audioLevel}%` }} /></div>
                    </div>
                  </label>
                </div>
                <div style={styles.sourceButtonRow}>
                  <button type="button" style={{ ...styles.sourceActionButton, ...styles.sourceActionButtonPrimary }} onClick={connectCameraAndMicrophone}><Video size={16} /> Connect Camera + Mic</button>
                </div>
              </div>
            </SectionCard>

            <SectionCard title="YouTube / Encoder" icon={CheckCircle2}>
              <div style={styles.encoderTagRow}>
                <span style={{ ...styles.encoderTag, ...(youtubeConnected ? styles.encoderTagReady : styles.encoderTagWarning) }}>
                  <span style={{ ...styles.encoderStatusDot, ...(youtubeConnected ? styles.encoderStatusDotGood : styles.encoderStatusDotWarning) }} />
                  <span style={styles.encoderTagText}>YouTube {youtubeConnected ? "connected" : "needed"}</span>
                </span>
                <span style={{ ...styles.encoderTag, ...(relayReady ? styles.encoderTagReady : styles.encoderTagWarning) }}>
                  <span style={{ ...styles.encoderStatusDot, ...(relayReady ? styles.encoderStatusDotGood : styles.encoderStatusDotWarning) }} />
                  <span style={styles.encoderTagText}>Oikos Relay Server {relayReady ? "ready" : "pending"}</span>
                </span>
              </div>
              {connection?.channelTitle ? (
                <p style={styles.channelLine}>{connection.channelTitle}</p>
              ) : null}
              <button type="button" style={styles.primaryButton} onClick={connectYouTube} disabled={youtubeBusy || !accountId}>
                {youtubeBusy ? "Opening..." : youtubeConnected ? "Reconnect YouTube" : "Connect YouTube"}
              </button>
            </SectionCard>

            <SectionCard title="Schedule" icon={CalendarPlus}>
              <label style={styles.field}>
                <span>Title</span>
                <input style={styles.input} value={draft.title} onChange={(event) => setDraft((current) => ({ ...current, title: event.target.value }))} />
              </label>
              <label style={styles.field}>
                <span>Start</span>
                <input type="datetime-local" style={styles.input} value={draft.scheduledStartTime} onChange={(event) => setDraft((current) => ({ ...current, scheduledStartTime: event.target.value }))} />
              </label>
              <label style={styles.field}>
                <span>Privacy</span>
                <select style={styles.input} value={draft.privacyStatus} onChange={(event) => setDraft((current) => ({ ...current, privacyStatus: event.target.value }))}>
                  <option value="unlisted">Unlisted</option>
                  <option value="public">Public</option>
                  <option value="private">Private</option>
                </select>
              </label>
              <button type="button" style={styles.controlButton} onClick={createStream} disabled={youtubeBusy || !canCreateStream}>
                {youtubeBusy ? "Working..." : activeEvent ? "Create Another Stream" : encoderOnlyMode ? "Create Encoder Session" : "Create Stream"}
              </button>
              {activeEvent ? (
                <div style={styles.activeBroadcast}>
                  <strong>{activeEvent.title}</strong>
                  <span>{activeEvent.youtubeWatchUrl ? <a href={activeEvent.youtubeWatchUrl} target="_blank" rel="noreferrer" style={styles.inlineLink}>Open watch page</a> : "Encoder session ready"}</span>
                </div>
              ) : null}
            </SectionCard>

            <SectionCard title="Health" icon={Activity}>
              <div style={styles.healthGrid}>
                <span>Browser</span><strong>{health.browser}</strong>
                <span>Relay</span><strong>{health.relay}</strong>
                <span>YouTube</span><strong>{health.youtube}</strong>
                <span>FPS</span><strong>{health.fps}</strong>
                <span>Bitrate</span><strong>{health.bitrate}</strong>
              </div>
            </SectionCard>
          </aside>

          <div style={styles.mainStage}>
            <section style={styles.previewPanel}>
              <div style={styles.previewToolbar}>
                <div style={styles.previewToolbarLeft}>
                  <Radio size={17} />
                  <strong>{selectedLayout.label}</strong>
                </div>
                <div style={styles.previewActions}>
                  <button type="button" style={styles.controlButton} onClick={startPreview}><Video size={16} /> Preview</button>
                  <div style={styles.goLiveSplit}>
                    <button type="button" style={styles.liveButton} onClick={requestGoLive} disabled={youtubeBusy || relayStreaming}>
                      <Radio size={16} /> {relayStreaming ? "Streaming" : "Go Live"}
                    </button>
                    <span ref={streamMenuRef} style={styles.liveEventMenuWrap}>
                      <button
                        type="button"
                        style={styles.liveEventMenuButton}
                        onClick={() => setShowStreamMenu((current) => !current)}
                        aria-label="Select scheduled stream"
                        title={activeEvent ? streamEventLabel(activeEvent) : "Select stream"}
                      >
                        <ChevronDown size={15} />
                      </button>
                      {showStreamMenu ? (
                        <div style={styles.liveEventMenu}>
                          {upcomingStreamEvents.length ? upcomingStreamEvents.map((event) => {
                            const selected = activeEvent?.id === event.id;
                            return (
                              <button
                                key={event.id}
                                type="button"
                                style={{ ...styles.liveEventMenuItem, ...(selected ? styles.liveEventMenuItemActive : {}) }}
                                onClick={() => {
                                  setActiveEvent(event);
                                  setStreamStatus(event.status === "live" ? "Live" : "Ready");
                                  setShowStreamMenu(false);
                                }}
                              >
                                <span style={styles.liveEventMenuTitle}>{event.title || "Untitled stream"}</span>
                                <span style={styles.liveEventMenuMeta}>{streamEventMenuMeta(event)}</span>
                              </button>
                            );
                          }) : (
                            <div style={styles.liveEventMenuEmpty}>No upcoming streams</div>
                          )}
                        </div>
                      ) : null}
                    </span>
                  </div>
                  <button type="button" style={styles.stopButton} onClick={endStream}><Square size={16} /> End</button>
                </div>
              </div>

              <div style={styles.previewFrame}>
                <canvas ref={canvasRef} width="1920" height="1080" style={styles.canvas} />
                {!previewActive ? (
                  <div style={styles.previewOverlay}>
                    <div style={styles.previewIdleLockup}>
                      <span className="stream-idle-logo-frame" style={styles.previewIdleLogoFrame}>
                        <img src={logoUrl} alt="" style={styles.previewIdleLogo} />
                      </span>
                      <div style={styles.previewIdleCopy}>
                        <strong>Preview idle</strong>
                        <span style={styles.previewIdleBrand}>Oikos Live Stream Studio</span>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>

              {missingWarnings.length ? (
                <div style={styles.warningList}>
                  {missingWarnings.map((warning) => (
                    <span key={warning} style={styles.warningText}>
                      <ShieldAlert size={15} /> {warning}
                    </span>
                  ))}
                </div>
              ) : null}
            </section>

            <section style={styles.toolsPanel}>
              <div style={styles.toolsHeader}>
                <div>
                  <p style={styles.toolsEyebrow}>Streaming Tools</p>
                </div>
              </div>

              <div style={styles.displayToolRow}>
                <button type="button" style={styles.displayToolButton} onClick={openLiveDisplaySource}>
                  <ExternalLink size={16} /> Connect Display
                </button>
                <button type="button" style={styles.displayToolButton} onClick={captureDisplay}>
                  <ScreenShare size={16} /> Capture
                </button>
              </div>

              <div style={styles.segmented}>
                {SCENE_LAYOUTS.map((layout) => (
                  <button
                    key={layout.id}
                    type="button"
                    style={{ ...styles.segmentButton, ...(layout.id === layoutId ? styles.segmentButtonActive : {}) }}
                    onClick={() => setLayoutId(layout.id)}
                  >
                    {layout.label}
                  </button>
                ))}
              </div>

              <div style={styles.scheduledStreamsPanel}>
                <div style={styles.scheduledStreamsHeader}>
                  <div>
                    <p style={styles.toolsEyebrow}>Scheduled Streams</p>
                    <h2 style={styles.scheduledStreamsTitle}>Upcoming</h2>
                  </div>
                  <span style={styles.scheduledStreamsCount}>{upcomingStreamEvents.length}</span>
                </div>

                {upcomingStreamEvents.length ? (
                  <div style={styles.scheduledStreamsGrid}>
                    {upcomingStreamEvents.map((event) => {
                      const selected = activeEvent?.id === event.id;
                      const startTime = getStreamEventStartTime(event);
                      const startDate = startTime ? new Date(startTime) : null;
                      return (
                        <button
                          key={event.id}
                          type="button"
                          style={{ ...styles.scheduledStreamTile, ...(selected ? styles.scheduledStreamTileActive : {}) }}
                          onClick={() => {
                            setActiveEvent(event);
                            setStreamStatus(event.status === "live" ? "Live" : "Ready");
                          }}
                        >
                          <span style={styles.scheduledStreamCopy}>
                            <strong style={styles.scheduledStreamTitle}>{event.title || "Untitled stream"}</strong>
                            <span style={styles.scheduledStreamMeta}>{startDate ? startDate.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" }) : "Date not set"}</span>
                            <span style={styles.scheduledStreamTime}>{startDate ? startDate.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "Time not set"}</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div style={styles.scheduledStreamsEmpty}>
                    Create a stream to see it here.
                  </div>
                )}
              </div>
            </section>

            <video ref={displayVideoRef} playsInline muted autoPlay style={styles.hiddenVideo} />
          </div>
        </div>
      </section>
    </main>
  );
}

const styles = {
  page: {
    background: "#f6f8f4",
    color: "#17251e",
    minHeight: "100dvh",
    padding: "24px",
  },
  pagePhone: {
    padding: "14px",
  },
  studio: {
    display: "grid",
    gap: 16,
    margin: "0 auto",
    maxWidth: "none",
  },
  studioPhone: {
    gap: 12,
  },
  topBar: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #dfe8db",
    borderRadius: 8,
    boxSizing: "border-box",
    boxShadow: "0 12px 30px rgba(31, 49, 38, 0.07)",
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    justifyContent: "space-between",
    minHeight: 68,
    padding: "10px 14px",
  },
  topBarEdge: {
    borderLeft: 0,
    borderRight: 0,
    borderTop: 0,
    borderRadius: "0 0 8px 8px",
    margin: "-24px -24px 0",
    paddingLeft: 18,
    paddingRight: 18,
    width: "auto",
  },
  topBarPhone: {
    alignItems: "stretch",
    borderLeft: 0,
    borderRight: 0,
    borderTop: 0,
    borderRadius: "0 0 8px 8px",
    flexDirection: "column",
    margin: "-14px -14px 0",
    paddingLeft: 14,
    paddingRight: 14,
    width: "auto",
  },
  studioGrid: {
    alignItems: "start",
    display: "grid",
    gap: 16,
    gridTemplateColumns: "300px minmax(0, 1fr)",
  },
  studioGridEdge: {
    marginLeft: -24,
    width: "calc(100% + 24px)",
  },
  studioGridPhone: {
    gridTemplateColumns: "1fr",
  },
  leftRail: {
    display: "grid",
    gap: 12,
    position: "sticky",
    top: 16,
  },
  leftRailPhone: {
    position: "static",
  },
  mainStage: {
    display: "grid",
    gap: 14,
    minWidth: 0,
  },
  previewPanel: {
    background: "#ffffff",
    border: "1px solid #dfe8db",
    borderRadius: 8,
    boxShadow: "0 18px 42px rgba(31, 49, 38, 0.08)",
    display: "grid",
    gap: 12,
    overflow: "hidden",
    padding: 14,
  },
  previewToolbar: {
    alignItems: "center",
    display: "flex",
    gap: 12,
    justifyContent: "space-between",
  },
  previewToolbarLeft: {
    alignItems: "center",
    color: "#26352c",
    display: "inline-flex",
    gap: 8,
    minWidth: 0,
  },
  previewActions: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    justifyContent: "flex-end",
  },
  errorLogWrap: {
    position: "relative",
  },
  errorLogTopButton: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #bfdbfe",
    borderRadius: 999,
    color: "#1d4ed8",
    cursor: "pointer",
    display: "inline-flex",
    fontSize: 11,
    fontWeight: 800,
    gap: 5,
    minHeight: 28,
    padding: "5px 8px",
    whiteSpace: "nowrap",
  },
  errorLogPopover: {
    background: "#ffffff",
    border: "1px solid #d7dfd2",
    borderRadius: 8,
    boxShadow: "0 20px 50px rgba(23, 37, 30, 0.18)",
    display: "grid",
    gap: 10,
    maxHeight: 360,
    overflow: "auto",
    padding: 12,
    position: "absolute",
    right: 0,
    top: "calc(100% + 8px)",
    width: 420,
    zIndex: 20,
  },
  errorLogHeader: {
    alignItems: "center",
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
  },
  errorLogClearButton: {
    background: "#f8faf7",
    border: "1px solid #dce6d6",
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 800,
    padding: "6px 9px",
  },
  errorLogList: {
    display: "grid",
    gap: 8,
  },
  errorLogItem: {
    background: "#fff7f7",
    border: "1px solid #f1caca",
    borderRadius: 8,
    display: "grid",
    gap: 5,
    padding: 9,
  },
  errorLogTime: {
    color: "#7a4b4b",
    fontSize: 11,
    fontWeight: 800,
  },
  errorLogMessage: {
    color: "#5f1717",
    font: "12px/1.35 ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
    margin: 0,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  errorLogEmpty: {
    color: "#647067",
    fontSize: 13,
    fontWeight: 700,
    margin: 0,
  },
  preLiveOverlay: {
    alignItems: "center",
    background: "radial-gradient(circle at 50% 42%, rgba(37, 99, 235, 0.22), rgba(15, 23, 42, 0.84) 46%, rgba(15, 23, 42, 0.94) 100%)",
    backdropFilter: "blur(12px)",
    color: "#ffffff",
    display: "grid",
    inset: 0,
    justifyItems: "center",
    padding: 24,
    position: "fixed",
    textAlign: "center",
    zIndex: 80,
  },
  preLiveLockup: {
    alignItems: "center",
    display: "grid",
    gap: 24,
    justifyItems: "center",
  },
  preLiveLogoFrame: {
    alignItems: "center",
    display: "flex",
    height: 150,
    justifyContent: "center",
    width: 150,
  },
  preLiveLogo: {
    display: "block",
    height: "100%",
    objectFit: "contain",
    width: "100%",
  },
  preLiveCopy: {
    display: "grid",
    gap: 8,
    justifyItems: "center",
  },
  preLiveEyebrow: {
    color: "#bfdbfe",
    fontSize: 18,
    fontWeight: 900,
    textTransform: "uppercase",
  },
  preLiveCount: {
    color: "#ffffff",
    fontSize: 118,
    fontWeight: 900,
    lineHeight: 0.9,
  },
  preLiveTitle: {
    color: "rgba(255, 255, 255, 0.82)",
    fontSize: 18,
    fontWeight: 900,
  },
  toolsPanel: {
    background: "#ffffff",
    border: "1px solid #dfe8db",
    borderRadius: 8,
    boxShadow: "0 14px 34px rgba(31, 49, 38, 0.07)",
    display: "grid",
    gap: 12,
    padding: 14,
  },
  toolsHeader: {
    alignItems: "center",
    display: "flex",
    gap: 12,
    justifyContent: "space-between",
  },
  toolsEyebrow: {
    color: "#1d4ed8",
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: 0,
    margin: 0,
    textTransform: "uppercase",
  },
  toolsTitle: {
    fontSize: 18,
    lineHeight: 1.15,
    margin: "3px 0 0",
  },
  displayToolRow: {
    alignItems: "center",
    display: "grid",
    gap: 8,
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  },
  displayToolButton: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #bfdbfe",
    borderRadius: 999,
    color: "#1d4ed8",
    cursor: "pointer",
    display: "inline-flex",
    fontSize: 12,
    fontWeight: 800,
    gap: 6,
    justifyContent: "center",
    minHeight: 38,
    padding: "9px 12px",
    whiteSpace: "nowrap",
    WebkitTapHighlightColor: "transparent",
  },
  hero: {
    alignItems: "center",
    display: "flex",
    gap: 18,
    justifyContent: "space-between",
    margin: "0 auto 18px",
    maxWidth: 1320,
  },
  heroIdentity: {
    alignItems: "center",
    display: "flex",
    flex: "1 1 auto",
    gap: 14,
    minWidth: 0,
  },
  heroCopy: {
    minWidth: 0,
  },
  logo: {
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    borderRadius: 10,
    boxSizing: "border-box",
    height: 42,
    objectFit: "contain",
    padding: 5,
    width: 42,
  },
  logoButton: {
    background: "transparent",
    border: 0,
    cursor: "pointer",
    display: "inline-flex",
    margin: 0,
    padding: 0,
  },
  eyebrow: {
    color: "#1d4ed8",
    fontSize: 12,
    fontWeight: 800,
    margin: 0,
    textTransform: "uppercase",
  },
  title: {
    fontSize: 24,
    lineHeight: 1.1,
    margin: "2px 0 0",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  subtitle: {
    color: "#647067",
    margin: 0,
  },
  statusRow: {
    alignItems: "center",
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    justifyContent: "flex-end",
    minWidth: 0,
    flex: "0 1 auto",
  },
  encoderTagRow: {
    display: "grid",
    gap: 8,
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  },
  encoderTag: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 999,
    boxSizing: "border-box",
    color: "#475569",
    display: "inline-flex",
    fontSize: 11,
    fontWeight: 800,
    gap: 5,
    height: 28,
    justifyContent: "center",
    minWidth: 0,
    padding: "5px 8px",
    whiteSpace: "nowrap",
    lineHeight: 1,
  },
  headerTagButton: {
    appearance: "none",
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: 11,
    fontWeight: 800,
    lineHeight: 1,
  },
  encoderTagText: {
    fontSize: 11,
    lineHeight: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  encoderTagReady: {
    background: "#eff6ff",
    borderColor: "#bfdbfe",
    color: "#1d4ed8",
  },
  encoderTagWarning: {
    background: "#fffbeb",
    borderColor: "#fde68a",
    color: "#92400e",
  },
  encoderTagDanger: {
    background: "#fee2e2",
    borderColor: "#fecaca",
    color: "#991b1b",
  },
  encoderStatusDot: {
    background: "#2563eb",
    borderRadius: 999,
    display: "inline-block",
    flex: "0 0 auto",
    height: 6,
    width: 6,
  },
  encoderStatusDotGood: {
    background: "#2563eb",
    boxShadow: "0 0 0 3px rgba(37, 99, 235, 0.12)",
  },
  encoderStatusDotWarning: {
    background: "#f59e0b",
    boxShadow: "0 0 0 3px rgba(245, 158, 11, 0.14)",
  },
  encoderStatusDotDanger: {
    background: "#991b1b",
    boxShadow: "0 0 0 3px rgba(153, 27, 27, 0.12)",
  },
  grid: {
    alignItems: "start",
    display: "grid",
    gap: 18,
    gridTemplateColumns: "minmax(0, 1.55fr) minmax(320px, 0.85fr)",
    margin: "0 auto",
    maxWidth: 1320,
  },
  gridPhone: {
    gridTemplateColumns: "1fr",
  },
  leftColumn: {
    display: "grid",
    gap: 18,
  },
  rightColumn: {
    display: "grid",
    gap: 18,
  },
  card: {
    background: "#ffffff",
    border: "1px solid #dfe8db",
    borderRadius: 8,
    boxShadow: "0 14px 34px rgba(31, 49, 38, 0.08)",
    display: "grid",
    gap: 14,
    padding: 16,
  },
  cardHeader: {
    alignItems: "center",
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
  },
  cardTitleWrap: {
    alignItems: "center",
    display: "flex",
    gap: 8,
  },
  cardTitle: {
    fontSize: 17,
    margin: 0,
  },
  previewFrame: {
    aspectRatio: "16 / 9",
    background: "#0f1b15",
    borderRadius: 8,
    overflow: "hidden",
    position: "relative",
    width: "100%",
  },
  canvas: {
    display: "block",
    height: "100%",
    width: "100%",
  },
  previewOverlay: {
    alignItems: "center",
    background: "radial-gradient(circle at 50% 38%, rgba(37, 99, 235, 0.22), rgba(15, 27, 21, 0.84) 48%, rgba(15, 27, 21, 0.94) 100%)",
    color: "#ffffff",
    display: "grid",
    inset: 0,
    justifyItems: "center",
    position: "absolute",
    textAlign: "center",
  },
  previewIdleLogoFrame: {
    alignItems: "center",
    display: "flex",
    height: 116,
    justifyContent: "center",
    width: 116,
  },
  previewIdleLockup: {
    alignItems: "center",
    display: "grid",
    gap: 20,
    justifyItems: "center",
    transform: "translateY(-2%)",
  },
  previewIdleLogo: {
    display: "block",
    height: "100%",
    objectFit: "contain",
    width: "100%",
  },
  previewIdleCopy: {
    display: "grid",
    fontSize: 22,
    fontWeight: 900,
    gap: 6,
    letterSpacing: 0,
    lineHeight: 1.05,
  },
  previewIdleBrand: {
    color: "#bfdbfe",
    fontSize: 13,
    fontWeight: 900,
    letterSpacing: 0,
    textTransform: "uppercase",
  },
  warningList: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
  warningText: {
    alignItems: "center",
    background: "#fff7dc",
    border: "1px solid #f6df9b",
    borderRadius: 8,
    color: "#745000",
    display: "inline-flex",
    fontSize: 12,
    fontWeight: 700,
    gap: 5,
    padding: "7px 9px",
  },
  sourceGrid: {
    display: "grid",
    gap: 12,
    gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
  },
  sourceDock: {
    alignItems: "stretch",
    display: "grid",
    gap: 12,
    gridTemplateColumns: "220px minmax(0, 1fr)",
  },
  sourceDockPhone: {
    gridTemplateColumns: "1fr",
  },
  sourceControls: {
    display: "grid",
    gap: 10,
    minWidth: 0,
  },
  sourceControlsHeader: {
    alignItems: "center",
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
  },
  compactFields: {
    display: "grid",
    gap: 10,
    gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
  },
  sourceButtonRow: {
    display: "grid",
    gap: 8,
  },
  sourceStatusRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
  field: {
    display: "grid",
    gap: 7,
    fontSize: 13,
    fontWeight: 800,
  },
  input: {
    background: "#f8faf7",
    border: "1px solid #dce6d6",
    borderRadius: 8,
    color: "#17251e",
    font: "inherit",
    minHeight: 40,
    padding: "9px 10px",
  },
  controlButton: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #bfdbfe",
    borderRadius: 999,
    color: "#1d4ed8",
    cursor: "pointer",
    display: "inline-flex",
    fontSize: 12,
    fontWeight: 800,
    gap: 6,
    justifyContent: "center",
    minHeight: 36,
    padding: "8px 12px",
    whiteSpace: "nowrap",
  },
  goLiveSplit: {
    alignItems: "stretch",
    display: "inline-flex",
    minWidth: 0,
  },
  liveEventMenuWrap: {
    alignItems: "center",
    display: "inline-flex",
    justifyContent: "center",
    minHeight: 36,
    position: "relative",
  },
  liveEventMenuButton: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #f0b8b8",
    borderLeft: 0,
    borderRadius: "0 999px 999px 0",
    color: "#9f1d1d",
    cursor: "pointer",
    display: "inline-flex",
    height: 36,
    justifyContent: "center",
    width: 38,
  },
  liveEventMenu: {
    background: "#ffffff",
    border: "1px solid #bfdbfe",
    borderRadius: 8,
    boxShadow: "0 18px 42px rgba(15, 23, 42, 0.16)",
    display: "grid",
    gap: 4,
    minWidth: 270,
    padding: 6,
    position: "absolute",
    right: 0,
    top: "calc(100% + 8px)",
    zIndex: 30,
  },
  liveEventMenuItem: {
    background: "#ffffff",
    border: "1px solid transparent",
    borderRadius: 8,
    color: "#17251e",
    cursor: "pointer",
    display: "grid",
    gap: 2,
    padding: "9px 10px",
    textAlign: "left",
  },
  liveEventMenuItemActive: {
    background: "#eff6ff",
    borderColor: "#bfdbfe",
  },
  liveEventMenuTitle: {
    fontSize: 12,
    fontWeight: 900,
  },
  liveEventMenuMeta: {
    color: "#64748b",
    fontSize: 11,
    fontWeight: 800,
  },
  liveEventMenuEmpty: {
    color: "#64748b",
    fontSize: 12,
    fontWeight: 800,
    padding: "10px 12px",
  },
  smallButton: {
    background: "#ffffff",
    border: "1px solid #bfdbfe",
    borderRadius: 999,
    color: "#1d4ed8",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 800,
    padding: "8px 12px",
    whiteSpace: "nowrap",
  },
  primaryButton: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #bfdbfe",
    borderRadius: 999,
    color: "#1d4ed8",
    cursor: "pointer",
    display: "inline-flex",
    fontSize: 12,
    fontWeight: 800,
    gap: 6,
    justifyContent: "center",
    minHeight: 36,
    padding: "8px 12px",
    whiteSpace: "nowrap",
  },
  sourceActionButton: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #bfdbfe",
    borderRadius: 999,
    color: "#1d4ed8",
    cursor: "pointer",
    display: "inline-flex",
    fontSize: 12,
    fontWeight: 800,
    gap: 6,
    justifyContent: "center",
    minHeight: 38,
    padding: "9px 12px",
    whiteSpace: "nowrap",
    width: "100%",
  },
  sourceActionButtonPrimary: {
    background: "#ffffff",
    borderColor: "#bfdbfe",
    color: "#1d4ed8",
  },
  cameraMonitor: {
    aspectRatio: "16 / 9",
    background: "radial-gradient(circle at 50% 42%, rgba(37, 99, 235, 0.2), #101b15 58%, #0b1210 100%)",
    border: "1px solid #dce6d6",
    borderRadius: 8,
    overflow: "hidden",
    position: "relative",
    width: "100%",
  },
  cameraMonitorVideo: {
    display: "block",
    height: "100%",
    objectFit: "cover",
    width: "100%",
  },
  cameraMonitorOverlay: {
    alignItems: "center",
    color: "rgba(255,255,255,0.86)",
    display: "grid",
    fontSize: 11,
    fontWeight: 900,
    gap: 8,
    inset: 0,
    justifyItems: "center",
    position: "absolute",
    textAlign: "center",
  },
  cameraIdleLockup: {
    alignItems: "center",
    display: "grid",
    gap: 9,
    justifyItems: "center",
    transform: "translateY(-2px)",
  },
  cameraIdleLogoFrame: {
    alignItems: "center",
    display: "flex",
    height: 62,
    justifyContent: "center",
    width: 62,
  },
  cameraIdleLogo: {
    display: "block",
    height: "100%",
    objectFit: "contain",
    width: "100%",
  },
  cameraIdleText: {
    color: "#bfdbfe",
    fontSize: 11,
    fontWeight: 900,
    textTransform: "uppercase",
  },
  liveButton: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #f0b8b8",
    borderRadius: "999px 0 0 999px",
    color: "#9f1d1d",
    cursor: "pointer",
    display: "inline-flex",
    fontSize: 12,
    fontWeight: 900,
    gap: 6,
    justifyContent: "center",
    minHeight: 36,
    padding: "8px 12px",
    whiteSpace: "nowrap",
  },
  stopButton: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #fecaca",
    borderRadius: 999,
    color: "#991b1b",
    cursor: "pointer",
    display: "inline-flex",
    fontSize: 12,
    fontWeight: 800,
    gap: 6,
    justifyContent: "center",
    minHeight: 36,
    padding: "8px 12px",
    whiteSpace: "nowrap",
  },
  segmented: {
    display: "grid",
    gap: 8,
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
  },
  segmentButton: {
    background: "#ffffff",
    border: "1px solid #bfdbfe",
    borderRadius: 999,
    color: "#1d4ed8",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 800,
    minHeight: 38,
    padding: "9px 12px",
    whiteSpace: "nowrap",
    WebkitTapHighlightColor: "transparent",
  },
  segmentButtonActive: {
    background: "#eff6ff",
    borderColor: "#bfdbfe",
    color: "#1d4ed8",
  },
  scheduledStreamsPanel: {
    borderTop: "1px solid #e5eee1",
    display: "grid",
    gap: 12,
    paddingTop: 14,
  },
  scheduledStreamsHeader: {
    alignItems: "center",
    display: "flex",
    gap: 12,
    justifyContent: "space-between",
  },
  scheduledStreamsTitle: {
    fontSize: 18,
    lineHeight: 1.15,
    margin: "3px 0 0",
  },
  scheduledStreamsCount: {
    alignItems: "center",
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    borderRadius: 999,
    color: "#1d4ed8",
    display: "inline-flex",
    fontSize: 12,
    fontWeight: 900,
    height: 28,
    justifyContent: "center",
    minWidth: 28,
    padding: "0 9px",
  },
  scheduledStreamsGrid: {
    display: "flex",
    gap: 8,
    overflowX: "auto",
    paddingBottom: 2,
  },
  scheduledStreamTile: {
    alignItems: "stretch",
    background: "#ffffff",
    border: "1px solid #dbeafe",
    borderRadius: 8,
    color: "#17251e",
    cursor: "pointer",
    display: "grid",
    flex: "0 0 220px",
    gap: 8,
    minHeight: 96,
    padding: 12,
    textAlign: "left",
  },
  scheduledStreamTileActive: {
    background: "#eff6ff",
    borderColor: "#93c5fd",
  },
  scheduledStreamCopy: {
    display: "grid",
    gap: 6,
    minWidth: 0,
  },
  scheduledStreamTitle: {
    color: "#17251e",
    fontSize: 14,
    lineHeight: 1.2,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  scheduledStreamMeta: {
    color: "#1d4ed8",
    fontSize: 12,
    fontWeight: 900,
  },
  scheduledStreamTime: {
    color: "#64748b",
    fontSize: 12,
    fontWeight: 800,
  },
  scheduledStreamsEmpty: {
    background: "#f8fafc",
    border: "1px dashed #cbd5e1",
    borderRadius: 8,
    color: "#64748b",
    fontSize: 13,
    fontWeight: 800,
    padding: 14,
    textAlign: "center",
  },
  controlGrid: {
    display: "grid",
    gap: 9,
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  },
  healthGrid: {
    display: "grid",
    gap: "8px 12px",
    gridTemplateColumns: "1fr auto",
    fontSize: 13,
  },
  channelLine: {
    color: "#647067",
    fontSize: 13,
    fontWeight: 800,
    margin: "2px 0 0",
  },
  activeBroadcast: {
    background: "#f8faf7",
    border: "1px solid #dce6d6",
    borderRadius: 8,
    display: "grid",
    fontSize: 13,
    gap: 5,
    padding: 10,
  },
  inlineLink: {
    color: "#1d4ed8",
    fontWeight: 800,
    textDecoration: "none",
  },
  audioMeter: {
    display: "grid",
    gap: 8,
  },
  inlineAudioMeter: {
    display: "grid",
    gap: 7,
    marginTop: 2,
  },
  inlineAudioLabel: {
    color: "#647067",
    fontSize: 12,
    fontWeight: 800,
  },
  audioTrack: {
    background: "#dbeafe",
    borderRadius: 999,
    height: 10,
    overflow: "hidden",
  },
  audioFill: {
    background: "#2563eb",
    display: "block",
    height: "100%",
  },
  mutedText: {
    color: "#647067",
    lineHeight: 1.55,
    margin: 0,
  },
  hiddenVideo: {
    height: 1,
    opacity: 0,
    pointerEvents: "none",
    position: "absolute",
    width: 1,
  },
};
